import React, { useState, useCallback } from 'react';
import { supabase } from '../../../supabaseClient';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Assessment as AssessmentIcon,
  LocalMall as LocalMallIcon,
  ViewList as ViewListIcon,
  MonetizationOn as MonetizationOnIcon,
  Factory as FactoryIcon
} from '@mui/icons-material';

const ProductData = ({ companyId }) => {
  const theme = useTheme();
  const [productData, setProductData] = useState({
    bestSellersByRevenue: [],
    bestSellersByUnits: [],
    brandAnalytics: [],
    categoryAnalytics: [],
    productMetrics: {
      totalProducts: 0,
      activeProducts: 0,
      totalRevenue: 0,
      totalUnitsSold: 0,
      totalBrands: 0,
      totalCategories: 0,
      averageProductPrice: 0,
      inHouseProducts: 0,
      privateLabelProducts: 0
    }
  });

  const fetchProductData = useCallback(async () => {
    try {
      // Fetch best sellers by revenue from invoice items
      const { data: revenueData, error: revenueError } = await supabase
        .from('invoice_items')
        .select(`
          product_id,
          products!inner(
            name, 
            sku,
            brand_id,
            category_id,
            brands:brand_id(id, name),
            product_categories:category_id(id, name)
          ),
          total_price,
          quantity,
          invoices!inner(company_id)
        `)
        .eq('invoices.company_id', companyId)
        .not('total_price', 'is', null)
        .not('product_id', 'is', null);

      if (revenueError) throw revenueError;

      // Process revenue data
      const revenueMap = {};
      const unitsMap = {};
      let totalRevenue = 0;
      let totalUnitsSold = 0;

      revenueData?.forEach(item => {
        const productName = item.products?.name || 'Unknown Product';
        const revenue = parseFloat(item.total_price) || 0;
        const units = parseFloat(item.quantity) || 0;

        if (!revenueMap[productName]) {
          revenueMap[productName] = { 
            product_name: productName, 
            total_revenue: 0, 
            total_units: 0,
            sku: item.products?.sku || ''
          };
        }
        if (!unitsMap[productName]) {
          unitsMap[productName] = { 
            product_name: productName, 
            total_revenue: 0, 
            total_units: 0,
            sku: item.products?.sku || ''
          };
        }

        revenueMap[productName].total_revenue += revenue;
        revenueMap[productName].total_units += units;
        unitsMap[productName].total_revenue += revenue;
        unitsMap[productName].total_units += units;

        totalRevenue += revenue;
        totalUnitsSold += units;
      });

      // Convert to arrays and sort
      const bestSellersByRevenue = Object.values(revenueMap)
        .sort((a, b) => b.total_revenue - a.total_revenue)
        .slice(0, 10);

      const bestSellersByUnits = Object.values(unitsMap)
        .sort((a, b) => b.total_units - a.total_units)
        .slice(0, 10);

      // Fetch comprehensive product data including variants
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select(`
          id, 
          is_active, 
          default_price,
          unit_price,
          variants,
          product_type,
          brand_id,
          category_id,
          brands:brand_id(id, name),
          product_categories:category_id(id, name)
        `)
        .eq('company_id', companyId);

      if (productsError) throw productsError;

      // Fetch product variants data
      const { data: variantsData, error: variantsError } = await supabase
        .from('product_variants')
        .select(`
          id,
          product_id,
          price,
          unit_price,
          is_active,
          products!inner(company_id)
        `)
        .eq('products.company_id', companyId)
        .eq('is_active', true);

      if (variantsError) console.warn('Variants fetch error:', variantsError);

      // Fetch brands data
      const { data: brandsData, error: brandsError } = await supabase
        .from('brands')
        .select('id, name, is_active')
        .eq('company_id', companyId);

      if (brandsError) throw brandsError;

      // Fetch categories data
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('product_categories')
        .select('id, name, is_active')
        .eq('company_id', companyId);

      if (categoriesError) throw categoriesError;

      // Calculate product metrics
      const totalProducts = productsData?.length || 0;
      const activeProducts = productsData?.filter(p => p.is_active).length || 0;
      const totalBrands = brandsData?.filter(b => b.is_active).length || 0;
      const totalCategories = categoriesData?.filter(c => c.is_active).length || 0;
      
      // Calculate average product price including variants
      const allPrices = [];
      
      // Add product default prices
      productsData?.forEach(product => {
        if (product.default_price && product.default_price > 0) {
          allPrices.push(parseFloat(product.default_price));
        }
        if (product.unit_price && product.unit_price > 0) {
          allPrices.push(parseFloat(product.unit_price));
        }
        
        // Add JSONB variant prices
        if (product.variants && Array.isArray(product.variants)) {
          product.variants.forEach(variant => {
            if (variant.price && variant.price > 0) {
              allPrices.push(parseFloat(variant.price));
            }
            if (variant.unit_price && variant.unit_price > 0) {
              allPrices.push(parseFloat(variant.unit_price));
            }
          });
        }
      });
      
      // Add product_variants table prices
      variantsData?.forEach(variant => {
        if (variant.price && variant.price > 0) {
          allPrices.push(parseFloat(variant.price));
        }
        if (variant.unit_price && variant.unit_price > 0) {
          allPrices.push(parseFloat(variant.unit_price));
        }
      });
      
      const averageProductPrice = allPrices.length > 0 
        ? allPrices.reduce((sum, price) => sum + price, 0) / allPrices.length 
        : 0;

      // Calculate product type distribution
      const inHouseProducts = productsData?.filter(p => p.product_type === 'In-House').length || 0;
      const privateLabelProducts = productsData?.filter(p => p.product_type === 'Private Label').length || 0;

      // Calculate brand performance from sales data
      const brandRevenue = {};
      revenueData?.forEach(item => {
        const brandName = item.products?.brands?.name || 'No Brand';
        const revenue = parseFloat(item.total_price) || 0;
        
        if (!brandRevenue[brandName]) {
          brandRevenue[brandName] = { brand_name: brandName, total_revenue: 0, product_count: 0 };
        }
        brandRevenue[brandName].total_revenue += revenue;
      });

      // Add product counts per brand
      productsData?.forEach(product => {
        const brandName = product.brands?.name || 'No Brand';
        if (!brandRevenue[brandName]) {
          brandRevenue[brandName] = { brand_name: brandName, total_revenue: 0, product_count: 0 };
        }
        brandRevenue[brandName].product_count += 1;
      });

      const brandAnalytics = Object.values(brandRevenue)
        .sort((a, b) => b.total_revenue - a.total_revenue)
        .slice(0, 5);

      // Calculate category performance
      const categoryRevenue = {};
      revenueData?.forEach(item => {
        const categoryName = item.products?.product_categories?.name || 'Uncategorized';
        const revenue = parseFloat(item.total_price) || 0;
        
        if (!categoryRevenue[categoryName]) {
          categoryRevenue[categoryName] = { category_name: categoryName, total_revenue: 0, product_count: 0 };
        }
        categoryRevenue[categoryName].total_revenue += revenue;
      });

      // Add product counts per category
      productsData?.forEach(product => {
        const categoryName = product.product_categories?.name || 'Uncategorized';
        if (!categoryRevenue[categoryName]) {
          categoryRevenue[categoryName] = { category_name: categoryName, total_revenue: 0, product_count: 0 };
        }
        categoryRevenue[categoryName].product_count += 1;
      });

      const categoryAnalytics = Object.values(categoryRevenue)
        .sort((a, b) => b.total_revenue - a.total_revenue)
        .slice(0, 5);

      setProductData({
        bestSellersByRevenue,
        bestSellersByUnits,
        brandAnalytics,
        categoryAnalytics,
        productMetrics: {
          totalProducts,
          activeProducts,
          totalRevenue,
          totalUnitsSold,
          totalBrands,
          totalCategories,
          averageProductPrice,
          inHouseProducts,
          privateLabelProducts
        }
      });

    } catch (err) {
      console.error('Error fetching product data:', err);
      setProductData({
        bestSellersByRevenue: [],
        bestSellersByUnits: [],
        brandAnalytics: [],
        categoryAnalytics: [],
        productMetrics: {
          totalProducts: 0,
          activeProducts: 0,
          totalRevenue: 0,
          totalUnitsSold: 0,
          totalBrands: 0,
          totalCategories: 0,
          averageProductPrice: 0,
          inHouseProducts: 0,
          privateLabelProducts: 0
        }
      });
    }
  }, [companyId]);

  React.useEffect(() => {
    if (companyId) {
      fetchProductData();
    }
  }, [companyId, fetchProductData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US').format(number);
  };

  return (
    <Box sx={{ mb: 6 }}>
      {/* Product Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  mr: 2
                }}>
                  <AssessmentIcon sx={{ fontSize: 24, color: 'success.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Product Revenue
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'success.main',
                mb: 1
              }}>
                {formatCurrency(productData.productMetrics.totalRevenue)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Total sales revenue
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  mr: 2
                }}>
                  <ShoppingCartIcon sx={{ fontSize: 24, color: 'info.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Units Sold
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'info.main',
                mb: 1
              }}>
                {formatNumber(productData.productMetrics.totalUnitsSold)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Total units sold
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  mr: 2
                }}>
                  <InventoryIcon sx={{ fontSize: 24, color: 'warning.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Total Products
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'warning.main',
                mb: 1
              }}>
                {formatNumber(productData.productMetrics.totalProducts)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                In catalog
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  mr: 2
                }}>
                  <TrendingUpIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Active Products
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'primary.main',
                mb: 1
              }}>
                {formatNumber(productData.productMetrics.activeProducts)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Currently active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Product Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha('#FF9800', 0.1),
                  mr: 2
                }}>
                  <MonetizationOnIcon sx={{ fontSize: 24, color: '#FF9800' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Average Price
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: '#FF9800',
                mb: 1
              }}>
                {formatCurrency(productData.productMetrics.averageProductPrice)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Per product
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha('#2196F3', 0.1),
                  mr: 2
                }}>
                  <LocalMallIcon sx={{ fontSize: 24, color: '#2196F3' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Active Brands
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: '#2196F3',
                mb: 1
              }}>
                {formatNumber(productData.productMetrics.totalBrands)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Brand portfolio
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha('#9C27B0', 0.1),
                  mr: 2
                }}>
                  <ViewListIcon sx={{ fontSize: 24, color: '#9C27B0' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Categories
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: '#9C27B0',
                mb: 1
              }}>
                {formatNumber(productData.productMetrics.totalCategories)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Product categories
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha('#FF5722', 0.1),
                  mr: 2
                }}>
                  <FactoryIcon sx={{ fontSize: 24, color: '#FF5722' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  In-House Products
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: '#FF5722',
                mb: 1
              }}>
                {formatNumber(productData.productMetrics.inHouseProducts)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                vs {formatNumber(productData.productMetrics.privateLabelProducts)} Private Label
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Product Analytics Grid */}
      <Grid container spacing={4}>
        {/* Best Sellers by Revenue Chart */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Best Sellers by Revenue
              </Typography>
              {productData.bestSellersByRevenue.length > 0 ? (
                <Box sx={{ 
                  height: 500, 
                  width: '100%',
                  bgcolor: 'white',
                  borderRadius: 2,
                  p: 2,
                  border: `1px solid ${theme.palette.grey[200]}`
                }}>
                  {/* Chart Container */}
                  <Box sx={{ 
                    display: 'flex', 
                    height: '100%'
                  }}>
                    {/* Chart Area with Y-Axis */}
                    <Box sx={{ 
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%'
                    }}>
                      {/* Bars Container with Y-Axis */}
                      <Box sx={{
                        flex: 1,
                        display: 'flex',
                        gap: 2
                      }}>
                        {/* Y-Axis Labels */}
                        <Box sx={{ 
                          width: 60,
                          display: 'flex',
                          flexDirection: 'column-reverse',
                          justifyContent: 'space-between',
                          pr: 1
                        }}>
                          {Array.from({ length: 6 }, (_, i) => {
                            const actualMax = Math.max(...productData.bestSellersByRevenue.map(p => p.total_revenue), 100);
                            // Round up to nice number (nearest 1000, 5000, 10000, etc)
                            const roundedMax = Math.ceil(actualMax / 1000) * 1000;
                            const niceMax = roundedMax <= 5000 ? Math.ceil(roundedMax / 1000) * 1000 : Math.ceil(roundedMax / 5000) * 5000;
                            const value = (niceMax / 5) * i;
                            return (
                              <Typography 
                                key={i} 
                                variant="caption" 
                                sx={{ 
                                  color: 'text.secondary',
                                  fontSize: '0.75rem',
                                  textAlign: 'right',
                                  lineHeight: 1
                                }}
                              >
                                ${value >= 1000 ? `${(value/1000)}K` : value}
                              </Typography>
                            );
                          })}
                        </Box>
                        
                        {/* Bars Container */}
                        <Box sx={{ 
                          flex: 1,
                          display: 'flex',
                          alignItems: 'flex-end',
                          gap: 1,
                          px: 2,
                          position: 'relative',
                          borderLeft: `2px solid ${theme.palette.grey[200]}`,
                          borderBottom: `2px solid ${theme.palette.grey[200]}`
                        }}>
                        {/* Grid Lines */}
                        {Array.from({ length: 5 }, (_, i) => (
                          <Box
                            key={i}
                            sx={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              bottom: `${i * 20}%`,
                              height: '1px',
                              bgcolor: alpha(theme.palette.grey[300], 0.5),
                              zIndex: 0
                            }}
                          />
                        ))}
                        
                        {/* Bars */}
                        {productData.bestSellersByRevenue.map((product, index) => {
                          const actualMax = Math.max(...productData.bestSellersByRevenue.map(p => p.total_revenue), 100);
                          const roundedMax = Math.ceil(actualMax / 1000) * 1000;
                          const niceMax = roundedMax <= 5000 ? Math.ceil(roundedMax / 1000) * 1000 : Math.ceil(roundedMax / 5000) * 5000;
                          const barHeight = (product.total_revenue / niceMax) * 100;
                          
                          return (
                            <Box 
                              key={index} 
                              sx={{ 
                                flex: 1,
                                height: '100%',
                                display: 'flex',
                                alignItems: 'flex-end',
                                justifyContent: 'center',
                                position: 'relative',
                                zIndex: 1,
                                maxWidth: '60px'
                              }}
                            >
                              {/* Tooltip */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: `${barHeight}%`,
                                  left: '50%',
                                  transform: 'translate(-50%, -8px)',
                                  bgcolor: 'rgba(0,0,0,0.9)',
                                  color: 'white',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontSize: '0.75rem',
                                  whiteSpace: 'nowrap',
                                  opacity: 0,
                                  pointerEvents: 'none',
                                  transition: 'opacity 0.2s ease',
                                  zIndex: 100,
                                  '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '100%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    borderLeft: '4px solid transparent',
                                    borderRight: '4px solid transparent',
                                    borderTop: '4px solid rgba(0,0,0,0.9)'
                                  }
                                }}
                                className="chart-tooltip"
                              >
                                <Box sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                  {product.product_name}
                                </Box>
                                <Box>{formatCurrency(product.total_revenue)}</Box>
                                <Box>{formatNumber(product.total_units)} units</Box>
                              </Box>
                              
                              {/* Bar */}
                              <Box
                                sx={{
                                  width: '80%',
                                  maxWidth: '40px',
                                  height: `${barHeight}%`,
                                  minHeight: '4px',
                                  bgcolor: '#4caf50',
                                  borderRadius: '4px 4px 0 0',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  '&:hover': {
                                    bgcolor: '#66bb6a',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  const tooltip = e.currentTarget.parentElement.querySelector('.chart-tooltip');
                                  if (tooltip) tooltip.style.opacity = '1';
                                }}
                                onMouseLeave={(e) => {
                                  const tooltip = e.currentTarget.parentElement.querySelector('.chart-tooltip');
                                  if (tooltip) tooltip.style.opacity = '0';
                                }}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                      </Box>
                      
                      {/* X-Axis Labels */}
                      <Box sx={{ 
                        display: 'flex',
                        gap: 1,
                        px: 2,
                        pt: 3,
                        ml: '64px',
                        minHeight: '60px'
                      }}>
                        {productData.bestSellersByRevenue.map((product, index) => (
                          <Box key={index} sx={{ 
                            flex: 1,
                            maxWidth: '60px',
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            height: '50px'
                          }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'text.secondary',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                transform: 'rotate(-45deg)',
                                transformOrigin: 'center bottom',
                                whiteSpace: 'nowrap',
                                width: 'max-content',
                                mt: 2
                              }}
                            >
                              {product.product_name}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ 
                  height: 300, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="h6">No product sales data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Best Sellers by Units Chart */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Best Sellers by Units Sold
              </Typography>
              {productData.bestSellersByUnits.length > 0 ? (
                <Box sx={{ 
                  height: 500, 
                  width: '100%',
                  bgcolor: 'white',
                  borderRadius: 2,
                  p: 2,
                  border: `1px solid ${theme.palette.grey[200]}`
                }}>
                  {/* Chart Container */}
                  <Box sx={{ 
                    display: 'flex', 
                    height: '100%'
                  }}>
                    {/* Chart Area with Y-Axis */}
                    <Box sx={{ 
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%'
                    }}>
                      {/* Bars Container with Y-Axis */}
                      <Box sx={{
                        flex: 1,
                        display: 'flex',
                        gap: 2
                      }}>
                        {/* Y-Axis Labels */}
                        <Box sx={{ 
                          width: 60,
                          display: 'flex',
                          flexDirection: 'column-reverse',
                          justifyContent: 'space-between',
                          pr: 1
                        }}>
                          {Array.from({ length: 6 }, (_, i) => {
                            const actualMax = Math.max(...productData.bestSellersByUnits.map(p => p.total_units), 100);
                            // Round up to nice number
                            const roundedMax = Math.ceil(actualMax / 100) * 100;
                            let niceMax;
                            if (roundedMax <= 100) niceMax = 100;
                            else if (roundedMax <= 500) niceMax = Math.ceil(roundedMax / 100) * 100;
                            else if (roundedMax <= 1000) niceMax = Math.ceil(roundedMax / 500) * 500;
                            else niceMax = Math.ceil(roundedMax / 1000) * 1000;
                            const value = (niceMax / 5) * i;
                            return (
                              <Typography 
                                key={i} 
                                variant="caption" 
                                sx={{ 
                                  color: 'text.secondary',
                                  fontSize: '0.75rem',
                                  textAlign: 'right',
                                  lineHeight: 1
                                }}
                              >
                                {value >= 1000 ? `${(value/1000)}K` : Math.round(value)}
                              </Typography>
                            );
                          })}
                        </Box>
                        
                        {/* Bars Container */}
                        <Box sx={{ 
                          flex: 1,
                          display: 'flex',
                          alignItems: 'flex-end',
                          gap: 1,
                          px: 2,
                          position: 'relative',
                          borderLeft: `2px solid ${theme.palette.grey[200]}`,
                          borderBottom: `2px solid ${theme.palette.grey[200]}`
                        }}>
                        {/* Grid Lines */}
                        {Array.from({ length: 5 }, (_, i) => (
                          <Box
                            key={i}
                            sx={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              bottom: `${i * 20}%`,
                              height: '1px',
                              bgcolor: alpha(theme.palette.grey[300], 0.5),
                              zIndex: 0
                            }}
                          />
                        ))}
                        
                        {/* Bars */}
                        {productData.bestSellersByUnits.map((product, index) => {
                          const actualMax = Math.max(...productData.bestSellersByUnits.map(p => p.total_units), 100);
                          const roundedMax = Math.ceil(actualMax / 100) * 100;
                          let niceMax;
                          if (roundedMax <= 100) niceMax = 100;
                          else if (roundedMax <= 500) niceMax = Math.ceil(roundedMax / 100) * 100;
                          else if (roundedMax <= 1000) niceMax = Math.ceil(roundedMax / 500) * 500;
                          else niceMax = Math.ceil(roundedMax / 1000) * 1000;
                          const barHeight = (product.total_units / niceMax) * 100;
                          
                          return (
                            <Box 
                              key={index} 
                              sx={{ 
                                flex: 1,
                                height: '100%',
                                display: 'flex',
                                alignItems: 'flex-end',
                                justifyContent: 'center',
                                position: 'relative',
                                zIndex: 1,
                                maxWidth: '60px'
                              }}
                            >
                              {/* Tooltip */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: `${barHeight}%`,
                                  left: '50%',
                                  transform: 'translate(-50%, -8px)',
                                  bgcolor: 'rgba(0,0,0,0.9)',
                                  color: 'white',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontSize: '0.75rem',
                                  whiteSpace: 'nowrap',
                                  opacity: 0,
                                  pointerEvents: 'none',
                                  transition: 'opacity 0.2s ease',
                                  zIndex: 100,
                                  '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '100%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    borderLeft: '4px solid transparent',
                                    borderRight: '4px solid transparent',
                                    borderTop: '4px solid rgba(0,0,0,0.9)'
                                  }
                                }}
                                className="chart-tooltip"
                              >
                                <Box sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                  {product.product_name}
                                </Box>
                                <Box>{formatNumber(product.total_units)} units</Box>
                                <Box>{formatCurrency(product.total_revenue)}</Box>
                              </Box>
                              
                              {/* Bar */}
                              <Box
                                sx={{
                                  width: '80%',
                                  maxWidth: '40px',
                                  height: `${barHeight}%`,
                                  minHeight: '4px',
                                  bgcolor: '#4caf50',
                                  borderRadius: '4px 4px 0 0',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  '&:hover': {
                                    bgcolor: '#66bb6a',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  const tooltip = e.currentTarget.parentElement.querySelector('.chart-tooltip');
                                  if (tooltip) tooltip.style.opacity = '1';
                                }}
                                onMouseLeave={(e) => {
                                  const tooltip = e.currentTarget.parentElement.querySelector('.chart-tooltip');
                                  if (tooltip) tooltip.style.opacity = '0';
                                }}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                      </Box>
                      
                      {/* X-Axis Labels */}
                      <Box sx={{ 
                        display: 'flex',
                        gap: 1,
                        px: 2,
                        pt: 3,
                        ml: '64px',
                        minHeight: '60px'
                      }}>
                        {productData.bestSellersByUnits.map((product, index) => (
                          <Box key={index} sx={{ 
                            flex: 1,
                            maxWidth: '60px',
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            height: '50px'
                          }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'text.secondary',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                transform: 'rotate(-45deg)',
                                transformOrigin: 'center bottom',
                                whiteSpace: 'nowrap',
                                width: 'max-content',
                                mt: 2
                              }}
                            >
                              {product.product_name}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ 
                  height: 300, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="h6">No product sales data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Brand Performance */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Top Brands by Revenue
              </Typography>
              {productData.brandAnalytics.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>Brand</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Products</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Revenue</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productData.brandAnalytics.map((brand, index) => (
                        <TableRow key={brand.brand_name} sx={{
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05)
                          }
                        }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Chip 
                                label={index + 1}
                                size="small"
                                sx={{ 
                                  mr: 1,
                                  bgcolor: alpha('#2196F3', 0.1),
                                  color: '#2196F3',
                                  fontWeight: 500,
                                  minWidth: 24
                                }}
                              />
                              {brand.brand_name}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>
                            {formatNumber(brand.product_count)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500, color: 'success.main' }}>
                            {formatCurrency(brand.total_revenue)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">No brand data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Category Performance */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Top Categories by Revenue
              </Typography>
              {productData.categoryAnalytics.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>Category</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Products</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Revenue</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productData.categoryAnalytics.map((category, index) => (
                        <TableRow key={category.category_name} sx={{
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05)
                          }
                        }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Chip 
                                label={index + 1}
                                size="small"
                                sx={{ 
                                  mr: 1,
                                  bgcolor: alpha('#9C27B0', 0.1),
                                  color: '#9C27B0',
                                  fontWeight: 500,
                                  minWidth: 24
                                }}
                              />
                              {category.category_name}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>
                            {formatNumber(category.product_count)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500, color: 'success.main' }}>
                            {formatCurrency(category.total_revenue)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">No category data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductData;